import './styles.scss';
import React from 'react';
import track from 'react-tracking';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';

import { BLOCKS } from '@contentful/rich-text-types';
import { Button, Container } from '@edx/paragon';
import { defineMessages, injectIntl } from 'gatsby-plugin-react-intl';
import { seoContentTypeDefaultProps, seoContentTypeShape } from '@utils/seo';
import { SECURE_PREVIEW_DIRECTORY } from '@utils/preview';
import * as speedCurve from '@utils/speedCurve';
import { formatRichText } from '@utils/formatting';
import { intlShape } from '@prospectus/common/types/intlShape';

import { HeroPropTypes } from '@prospectus/common/ui/Hero/propTypes';
import Layout from '@prospectus/common/ui/Layout';
import SEOPage from '@prospectus/seo/ui/SEOPage';
import { SEOItemList } from '@prospectus/seo/ui/SEOItemList';
import ContentfulModule from '@prospectus/modular-page/modules/ContentfulModule';
import ContentfulModulesPropType from '@prospectus/modular-page/modules/ContentfulModulesPropType';
import SpeedCurveSnippet from '@prospectus/common/ui/SpeedCurveSnippet';
import ProspectusLink from '@prospectus/common/ui-strict/ProspectusLink';
import Breadcrumbs from '@prospectus/common/ui/PageTitleBreadcrumbs';
import ArticleStructuredData from '../../components/ArticleStructuredData';
import SERPListItemShape from '../../../common/types/SERPListItemShape';

const heroTitleOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => <h1 className="display-1">{children}</h1>,
  },
};

const messages = defineMessages({
  'prospectus.resource-page.table-of-contents.title': {
    id: 'prospectus.resource-page.table-of-contents.title',
    description: 'Title heading for the table of contents',
    defaultMessage: 'Table of Contents',
  },
});

function ResourcePage({ location: { pathname }, pageContext, intl }) {
  const {
    hero,
    byline,
    publicationDate,
    tagList,
    seo,
    modules,
    languageCode,
    tableOfContentsEntries,
    courseSEOItemList,
  } = pageContext;

  const includeSpeedCurveSnippet = pathname && pathname.includes('/resources/') && !pathname.includes(SECURE_PREVIEW_DIRECTORY);
  const headline = hero.title ? documentToPlainTextString(JSON.parse(hero.title.raw)) : seo.pageTitle;

  const breadCrumbs = [
    {
      label: 'Idea Hub',
      href: '/resources',
    },
    {
      label: seo.pageTitle,
    },
  ];

  return (
    <Layout
      title={seo.pageTitle}
      lang={languageCode}
    >
      <ArticleStructuredData
        headline={headline}
        datePublished={publicationDate}
        dateModified={publicationDate}
        byline={byline}
      />
      <Container className="resource-page py-5.5" size="lg">
        {includeSpeedCurveSnippet && <SpeedCurveSnippet pageLabel={speedCurve.SPEEDCURVE_LABEL_RESOURCE_PAGE} />}
        <Breadcrumbs breadCrumbs={breadCrumbs} pageTitle={seo.pageTitle} textColor="dark" />
        {seo
        && (
          <SEOPage
            meta={({
              title: seo.pageTitle,
              description: seo.pageDescription.pageDescription,
              pathname,
              robots: seo.robotsMetaTagContent,
            }
            )}
          />
        )}
        { courseSEOItemList && courseSEOItemList.length
          ? (
            <SEOItemList itemList={courseSEOItemList} />
          )
          : null}
        {hero && formatRichText(hero?.title?.raw, heroTitleOptions)}
        {tableOfContentsEntries?.at(0) && (
          <div className="resource-page-toc mt-4.5">
            <p className="h3 mb-4.5">
              {intl.formatMessage(messages['prospectus.resource-page.table-of-contents.title'])}
            </p>
            <ul className="my-4.5">
              {tableOfContentsEntries.map(entry => (
                <li key={entry.id} className="mt-1">
                  <ProspectusLink
                    href={`#${entry.id}`}
                    allowFragment
                    analytics={{
                      eventName: 'edx.bi.resource-page.table-of-contents.link.clicked',
                    }}
                  >
                    {entry?.linkText}
                  </ProspectusLink>
                </li>
              ))}
            </ul>
          </div>
        )}
        {modules && modules.at(0) && modules.map(module => (
          <ContentfulModule
            key={module.id}
            module={{
              ...module,
              theme: 'full-width',
            }}
            pageTitle={seo.pageTitle}
          />
        ))}
        {tagList && (
          <div className="resource-page-tags mt-5.5 d-flex flex-wrap">
            {tagList.map(tag => (
              <Button
                key={uuidv4()}
                variant="light"
                size="sm"
                className="resource-page-tag text-uppercase"
              >
                {tag}
              </Button>
            ))}
          </div>
        )}
      </Container>
    </Layout>
  );
}

const mapStateToProps = store => ({
  userLanguage: store.userLanguage,
});

ResourcePage.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  pageContext: PropTypes.shape({
    modules: PropTypes.arrayOf(ContentfulModulesPropType).isRequired,
    byline: PropTypes.string.isRequired,
    publicationDate: PropTypes.string.isRequired,
    tagList: PropTypes.arrayOf(PropTypes.string).isRequired,
    hero: PropTypes.shape(HeroPropTypes).isRequired,
    seo: seoContentTypeShape,
    languageCode: PropTypes.string.isRequired,
    tableOfContentsEntries: PropTypes.arrayOf(PropTypes.shape({
      linkText: PropTypes.string,
      id: PropTypes.string,
    })),
    courseSEOItemList: PropTypes.arrayOf(SERPListItemShape),
  }),
  intl: intlShape.isRequired,
};

ResourcePage.defaultProps = {
  pageContext: {
    seo: seoContentTypeDefaultProps,
    tableOfContentsEntries: [],
    courseSEOItemList: [],
  },
};

const ResourcePageTemplate = connect(mapStateToProps)(ResourcePage);

export default track(({ location }) => ({
  page: 'resource page',
  mobile: false,
  url: location.href,
}))(injectIntl(ResourcePageTemplate));
